/**
 * Notiz-Signal auf Anfragen-Karte und -Liste (Antoine, 25.10.2026, Mockup Variante A
 * „Zeile mit Vorschau“, freigegeben).
 *
 * `NotizZeile`: eine Zeile mit Icon, „1 Notiz“/„2 Notizen“, gekürzter Vorschau der jüngsten
 * Notiz und rechts klein „Autor · Datum“. Klick öffnet den Vorgang auf der Notizen-Karte
 * (`?notiz=1`, dasselbe Ziel wie der Menüpunkt „Notiz hinzufügen“).
 * `NotizZaehler`: das dezente Signal für die Listenansicht (Icon + Zahl).
 *
 * Beide erscheinen nur bei `notizAnzahl > 0`. Der Notiztext ist Nutzereingabe: immer als
 * Text-Node, nie als HTML.
 */
import { Link } from "react-router-dom";
import { Icon, ZEILEN_AKTION } from "./components";
import type { Inquiry } from "./types";
import { vorgangPfad } from "../lib/pfade";

/** Ein Tag und Monat reichen auf der Karte: „22.10.“. */
function kurzDatum(iso: string | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

export function notizBeschriftung(anzahl: number): string {
  return anzahl === 1 ? "1 Notiz" : `${anzahl} Notizen`;
}

export function notizZiel(anfrage: Pick<Inquiry, "id">): string {
  return `${vorgangPfad(anfrage.id)}?notiz=1`;
}

export function NotizZeile({ anfrage }: { anfrage: Inquiry }) {
  const anzahl = anfrage.notizAnzahl ?? 0;
  if (anzahl <= 0) return null;
  const letzte = anfrage.letzteNotiz;
  const datum = kurzDatum(letzte?.am);
  const urheber = [letzte?.autor, datum].filter(Boolean).join(" · ");
  return (
    <Link
      to={notizZiel(anfrage)}
      data-testid="karte-notizzeile"
      aria-label={`${notizBeschriftung(anzahl)}, Vorgang auf Notizen öffnen`}
      title="Vorgang auf Notizen öffnen"
      onClick={(e) => e.stopPropagation()}
      className={`${ZEILEN_AKTION} flex items-center gap-2 rounded-[9px] border border-[#EBD6A4] bg-[#FBF1DA] px-[9px] py-1.5 no-underline motion-safe:transition-[filter] hover:brightness-[0.97] dark:border-[#EBC373]/[0.28] dark:bg-[#EBC373]/[0.12]`}
    >
      <Icon name="sticky_note_2" className="shrink-0 text-[17px] text-[#8A5A12] dark:text-[#EBC373]" />
      <span className="whitespace-nowrap text-[12px] font-extrabold text-[#8A5A12] dark:text-[#EBC373]">
        {notizBeschriftung(anzahl)}
      </span>
      {letzte?.text && (
        <span data-testid="karte-notiz-vorschau" className="min-w-0 flex-1 truncate font-body-sm text-[12px] text-on-surface-variant dark:text-secondary-fixed-dim">
          {letzte.text}
        </span>
      )}
      {!letzte?.text && <span className="flex-1" />}
      {urheber && (
        <span data-testid="karte-notiz-urheber" className="hidden shrink-0 whitespace-nowrap font-body-sm text-[10.5px] text-on-surface-variant/70 sm:inline">
          {urheber}
        </span>
      )}
      <Icon name="chevron_right" className="shrink-0 text-[15px] text-on-surface-variant/70" />
    </Link>
  );
}

export function NotizZaehler({ anfrage }: { anfrage: Inquiry }) {
  const anzahl = anfrage.notizAnzahl ?? 0;
  if (anzahl <= 0) return null;
  return (
    <Link
      to={notizZiel(anfrage)}
      data-testid="liste-notizzaehler"
      aria-label={`${notizBeschriftung(anzahl)}, Vorgang auf Notizen öffnen`}
      title={anfrage.letzteNotiz?.text ? `${notizBeschriftung(anzahl)}: ${anfrage.letzteNotiz.text}` : notizBeschriftung(anzahl)}
      onClick={(e) => e.stopPropagation()}
      className={`${ZEILEN_AKTION} inline-flex items-center gap-0.5 rounded-full border border-[#EBD6A4] bg-[#FBF1DA] py-px pl-1.5 pr-2 text-[11.5px] font-extrabold text-[#8A5A12] no-underline hover:brightness-[0.97] dark:border-[#EBC373]/[0.28] dark:bg-[#EBC373]/[0.12] dark:text-[#EBC373]`}
    >
      <Icon name="sticky_note_2" className="text-[14px]" />
      {anzahl}
    </Link>
  );
}
